'use client';

import { Badge } from '@/components/ui/badge';
import type { SourceRunStatus } from '@/lib/sources/source-types';
import { formatRelativeTime } from '@/lib/projects/project-utils';

interface SourceStatusListProps {
  sourceStatuses?: SourceRunStatus[];
}

export function SourceStatusList({ sourceStatuses }: SourceStatusListProps) {
  const failedSources = sourceStatuses?.filter((s) => s.status === 'error') ?? [];
  const warningSources = sourceStatuses?.filter((s) => s.status === 'warning') ?? [];

  return (
    <>
      {failedSources.length > 0 && (
        <div className="mb-6 space-y-2">
          <p className="text-sm font-medium text-muted-foreground mb-2">Failed sources</p>
          <div className="space-y-2">
            {failedSources.map((source) => (
              <div
                key={source.sourceId}
                className="flex items-start justify-between gap-3 rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium">{source.name}</span>
                    <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                      Error
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{source.domain}</p>
                  {source.message && (
                    <p className="text-xs text-muted-foreground mt-0.5">{source.message}</p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatRelativeTime(source.lastRunAt)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {warningSources.length > 0 && (
        <div className="mb-6 space-y-2">
          <p className="text-sm font-medium text-muted-foreground mb-2">Warnings</p>
          <div className="space-y-2">
            {warningSources.map((source) => (
              <div
                key={source.sourceId}
                className="flex items-start justify-between gap-3 rounded-md border border-yellow-500/20 bg-yellow-500/5 px-3 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium">{source.name}</span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-yellow-500/50 text-yellow-600">
                      Warning
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{source.domain}</p>
                  {source.message && (
                    <p className="text-xs text-muted-foreground mt-0.5">{source.message}</p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {formatRelativeTime(source.lastRunAt)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
